import { PostReportData, ReportMedia } from 'features/reports/report.types';
import { LocationCreateUpdateData } from 'features/locations/location.types';
import { RelatedFlooding } from 'features/flooding/flooding.types';
import { RelatedWildfire } from 'features/wildfires/wildfireGeoEventData.types';
import { WildfireCreateUpdateData } from 'features/wildfires/wildfire.types';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';

export const ProvisionalSubmissionType = {
  CreateGeoEvent: 'create_geo_event',
  EditGeoEvent: 'edit_geo_event',
  CreateReport: 'create_report',
} as const;

// Unchanged location fields are omitted, so approval can't revert a bot update.
export type SubmittedWildfireEditData = Omit<
  WildfireCreateUpdateData,
  'lat' | 'lng' | 'regions'
> & {
  lat?: number;
  lng?: number;
  regions?: GeoEventRegion[];
};

export type GeoEventCreateUpdateData =
  | WildfireCreateUpdateData
  | SubmittedWildfireEditData
  | LocationCreateUpdateData;

// In a create-geo-event response the backend hydrates a location's parentGeoEvents
// into full related events, unlike the request payload which carries id references only.
export type SubmittedLocationData = Omit<
  LocationCreateUpdateData,
  'parentGeoEvents'
> & {
  parentGeoEvents: (RelatedWildfire | RelatedFlooding)[];
};

export type SubmittedGeoEventData =
  | WildfireCreateUpdateData
  | SubmittedLocationData;

export type ProvisionalSubmissionPayload =
  | {
      submissionType: typeof ProvisionalSubmissionType.CreateGeoEvent;
      submittedData: GeoEventCreateUpdateData;
      skipRedundancyCheck?: boolean;
    }
  | {
      submissionType: typeof ProvisionalSubmissionType.EditGeoEvent;
      submittedData: GeoEventCreateUpdateData;
      skipRedundancyCheck?: boolean;
    }
  | {
      submissionType: typeof ProvisionalSubmissionType.CreateReport;
      submittedData: PostReportData;
      skipRedundancyCheck?: boolean;
    };

export type ProvisionalSubmissionCreatedBy = {
  id: number;
  firstName: string;
  lastName: string;
};

type ProvisionalSubmissionResponseBase = {
  id: number;
  status: 'pending' | 'approved' | 'rejected';
  dateCreated: string;
  createdBy: ProvisionalSubmissionCreatedBy;
};

export type CreateGeoEventProvisionalSubmissionResponse =
  ProvisionalSubmissionResponseBase & {
    submissionType: typeof ProvisionalSubmissionType.CreateGeoEvent;
    submittedData: SubmittedGeoEventData;
    geoEventId?: number;
    media?: ReportMedia[];
  };

export type EditGeoEventProvisionalSubmissionResponse =
  ProvisionalSubmissionResponseBase & {
    submissionType: typeof ProvisionalSubmissionType.EditGeoEvent;
    submittedData: SubmittedWildfireEditData;
    geoEventId: number;
    media?: ReportMedia[];
  };

export type EditLocationProvisionalSubmissionResponse =
  ProvisionalSubmissionResponseBase & {
    submissionType: typeof ProvisionalSubmissionType.EditGeoEvent;
    submittedData: SubmittedLocationData;
    geoEventId: number;
  };

export type CreateReportProvisionalSubmissionResponse =
  ProvisionalSubmissionResponseBase & {
    submissionType: typeof ProvisionalSubmissionType.CreateReport;
    // A submission under review must reference an existing geoEvent, so
    // geoEventId is non-null here even though PostReportData allows null
    // for the create-from-scratch case.
    submittedData: Omit<PostReportData, 'geoEventId'> & { geoEventId: number };
    media: ReportMedia[];
  };

export type ProvisionalSubmissionResponse =
  | CreateGeoEventProvisionalSubmissionResponse
  | EditGeoEventProvisionalSubmissionResponse
  | EditLocationProvisionalSubmissionResponse
  | CreateReportProvisionalSubmissionResponse;
